import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, switchMap, catchError, withLatestFrom, take } from 'rxjs/operators';
import { DataApiService } from './data-api.service';
import * as dataActions from './data.actions';
import * as workflowSelectors from '../workflow/workflow.selectors';
import * as workflowActions from '../workflow/workflow.actions';
import * as datasetSelectors from '../datasets/dataset.selectors';
import * as dataSelectors from './data.selectors';
import * as fromAuth from 'src/app/modules/authentication/store/auth/auth.selectors';
import { TranslateService } from '@ngx-translate/core';
import {
  Document,
  FieldDataType,
  Dataset,
  DocumentReader,
  DocumentWriter,
  DocumentQueryParams,
  FilterGroup,
  FilterGroupType,
  RuleOperator,
  DocumentSchema,
  SystemFieldNames
} from '@app/modules/core/store/models';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import { UserContext } from 'src/app/modules/authentication/store/auth/user-context.model';
import { Workflow } from '../workflow';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

export function createSearchTermFilterGroup(params: DocumentQueryParams, dataset: Dataset, searchTerm: string): FilterGroup {
  if (!searchTerm?.length) {
    return null;
  }

  const searchFields = dataset?.schema?.fields?.filter(
    f =>
      f.dataType === FieldDataType.Text ||
      f.dataType === FieldDataType.Email ||
      f.dataType === FieldDataType.Phone ||
      f.dataType === FieldDataType.AutoIncrement
  );

  if (!(searchFields.length > 0)) {
    return null;
  }

  const searchFilterGroup: FilterGroup = {
    uId: uuidv4(),
    filterGroups: [],
    filters: [],
    groupType: FilterGroupType.Or
  };

  searchFields.forEach(f => {
    searchFilterGroup.filters.push({
      uId: uuidv4(),
      field: f.systemName,
      operator: RuleOperator.ContainsNoCase,
      value: searchTerm,
      dataType: null,
      displayValue: null,
      options: null
    });
  });

  const newFilterGroup: FilterGroup = {
    filterGroups: [searchFilterGroup],
    filters: [],
    groupType: FilterGroupType.And,
    uId: uuidv4()
  };

  const hasTranslations = dataset?.hasTranslations || dataset?.schema?.fields?.some(f => f.settings?.translatable === true);

  if (hasTranslations) {
    return {
      filterGroups: [newFilterGroup],
      filters: [
        {
          uId: uuidv4(),
          field: SystemFieldNames.cultureCode,
          operator: RuleOperator.Equal,
          value: this.cultureService?.currentLang
        }
      ],
      groupType: FilterGroupType.And,
      uId: uuidv4()
    };
  } else {
    return newFilterGroup;
  }
}

@Injectable()
export class DataEffects {
  constructor(
    private actions$: Actions,
    private dataApiService: DataApiService,
    private store$: Store<any>,
    private translate: TranslateService,
    private notify: SystemNotificationsService
  ) {}

  getDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dataActions.getDocuments, dataActions.paginateList, dataActions.setDocumentQueryParams),
      withLatestFrom(this.store$.select(datasetSelectors.selectSelectedDataset), this.store$.select(dataSelectors.selectQueryParams)),
      switchMap(([, dataset, queryParams]) => {
        return this.dataApiService.getDocuments(dataset?.uId, queryParams).pipe(
          map(results => {
            if (results?.totalCount > 0 && results?.skip >= results?.totalCount) {
              const query: DocumentQueryParams = {
                ...queryParams,
                pagination: {
                  ...queryParams.pagination,
                  skip: queryParams.pagination.skip - queryParams.pagination.take
                }
              };
              return dataActions.setDocumentQueryParams({ query });
            }

            return dataActions.getDocumentsComplete({ results });
          }),
          catchError(err => of(dataActions.setError({ err })))
        );
      })
    )
  );

  searchDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dataActions.setSearchTerm),
      withLatestFrom(this.store$.select(dataSelectors.selectQueryParams), this.store$.select(datasetSelectors.selectSelectedDataset)),
      map(([{ searchTerm }, params, dataset]) => {
        const query = {
          pagination: {
            skip: 0,
            take: 10
          },
          fields: null,
          filterGroup: createSearchTermFilterGroup(params, dataset, searchTerm),
          calcFields: null
        } as DocumentQueryParams;

        return dataActions.setDocumentQueryParams({ query });
      })
    )
  );

  updateDocWfSteps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dataActions.updateDocumentWfStep),
      withLatestFrom(
        this.store$.select(datasetSelectors.selectSelectedDatasetUID),
        this.store$.select(workflowSelectors.selectOldStepMigrationUId),
        this.store$.select(workflowSelectors.selectStepMigrationUId)
      ),
      switchMap(([, datasetUId, oldStepUId, stepUId]) => {
        return this.dataApiService.updateDocumentWorkflowSteps(datasetUId, oldStepUId, stepUId).pipe(
          map(() => dataActions.updateDocumentWfStepComplete()),
          catchError(err => of(dataActions.setError({ err })))
        );
      })
    )
  );

  newDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dataActions.newDocument),
      withLatestFrom(
        this.store$.select(datasetSelectors.selectSelectedDataset),
        this.store$.select(workflowSelectors.selectSelectedWorkflow)
      ),
      switchMap(([action, dataset, workflow]) => {
        if (!workflow) {
          this.store$.dispatch(workflowActions.getWorkflowForDataset({ datasetUId: dataset.uId }));
          this.store$
            .select(workflowSelectors.selectSelectedWorkflow)
            .pipe(take(1))
            .subscribe(wf => (workflow = wf));
        }

        const view = workflow.views.find(vw => vw.name === 'detail');

        if (!view) {
          let message = '';
          this.translate.get('ERRORS.DETAIL_FORM_MISSING').subscribe(val => (message = val));
          return [dataActions.setError({ err: message })];
        }

        const document = initDatasetDocument(dataset);

        return [dataActions.newDocumentComplete({ view, document })];
      })
    )
  );

  editDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dataActions.editDocument),
      withLatestFrom(this.store$.select(workflowSelectors.selectSelectedWorkflow)),
      switchMap(([action, workflow]) => {
        const view = workflow.views.find(vw => vw.name === 'detail');

        if (!view) {
          let message = '';
          this.translate.get('ERRORS.DETAIL_FORM_MISSING').subscribe(val => (message = val));
          return [dataActions.setError({ err: message })];
        }

        return [dataActions.editDocumentComplete({ view, document: action.document })];
      })
    )
  );

  deleteDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dataActions.deleteSelectedDocument),
      withLatestFrom(this.store$.select(datasetSelectors.selectSelectedDataset), this.store$.select(dataSelectors.selectDocumentForDelete)),
      switchMap(([, dataset, document]) => {
        const datasetUId = dataset.uId;

        return this.dataApiService.deleteDocument(datasetUId, document).pipe(
          map(() => dataActions.getDocuments()),
          catchError(err => of(dataActions.setError({ err })))
        );
      })
    )
  );

  clearDatasetData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dataActions.clearDatasetData),
      withLatestFrom(this.store$.select(datasetSelectors.selectSelectedDataset)),
      switchMap(([, dataset]) =>
        this.dataApiService.clearDatasetData(dataset.uId).pipe(
          map(() => {
            this.notify.success(this.translate.instant('MANAGEMENT.DATASETS.DATA-CLEARED'));
            return dataActions.getDocuments();
          }),
          catchError(err => of(dataActions.setError({ err })))
        )
      )
    )
  );

  saveDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dataActions.saveDocument),
      withLatestFrom(
        this.store$.select(datasetSelectors.selectSelectedDataset),
        this.store$.select(workflowSelectors.selectSelectedWorkflow),
        this.store$.select(fromAuth.selectUserContext)
      ),
      switchMap(([action, { uId: datasetUId }, workflow, userContext]) => {
        const docToSave = populateDefaults(action.document, workflow, userContext);

        const apiCall = action.isNewDocument
          ? this.dataApiService.createDocument(datasetUId, docToSave)
          : this.dataApiService.updateDocument(datasetUId, docToSave);

        return apiCall.pipe(
          map(() => dataActions.getDocuments()),
          catchError(err => of(dataActions.setError({ err })))
        );
      })
    )
  );
}

const initDatasetDocument = (dataset: Dataset): Document => {
  const document: Document = {};

  for (const field of dataset?.schema?.fields) {
    document[field.systemName] = null;
  }

  return document;
};

export const populateDefaults = (document: Document, workflow: Workflow, userContext: UserContext, cultureCode?: string): Document => {
  const writer = new DocumentWriter(document);
  const reader = new DocumentReader(document);

  if (!reader.uId) {
    writer.uId = uuidv4();
  }

  if (!reader.dateCreated) {
    writer.dateCreated = moment();
  }

  if (!reader.createdBy) {
    writer.createdBy = userContext.userProfileUId;
  }

  writer.dateUpdated = moment();
  writer.updatedBy = userContext.userProfileUId;

  if (!reader.workflowUId) {
    writer.workflowUId = workflow.uId;
  }

  if (!reader.workflowStepUId) {
    writer.workflowStepUId = workflow.workflowSteps?.length > 0 ? workflow.workflowSteps[0].uId : null;
  }

  if (!!cultureCode) {
    writer.cultureCode = cultureCode;
  }

  return writer.document;
};
